import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { t, FLAG_TONES } from "../format"
import { Sparkline } from "./Charts"
import { GRID, Figures } from "./Figures"
import ProjectDetail from "./ProjectDetail"


const SORTS = {
  drift:    row => row.projected_variance_cents || 0,
  name:     row => row.name.toLowerCase(),
  sold:     row => row.sold,
  planned:  row => row.planned,
  done:     row => row.done,
  consumed: row => row.consumed ?? -1,
  performance: row => row.performance ?? 99,
  projected: row => row.projected,
}

const HeaderCell = ({ sortKey, sort, onSort, align = "right", children }) => {
  const active = sort.key === sortKey
  return (
    <button
      type      = "button"
      onClick   = {() => onSort(sortKey)}
      className = {`flex items-center gap-1 uppercase tracking-wider text-[10.5px] font-medium hover:text-slate-800 ${
        align === "right" ? "justify-end text-right" : "justify-start"} ${active ? "text-slate-900" : "text-slate-500"}`}
    >
      {children}
      {active && <FontAwesomeIcon icon={sort.desc ? "arrow-down" : "arrow-up"} className="w-2 h-2" />}
    </button>
  )
}

const ProjectRow = ({ row, currency, open, onToggle }) => {
  const tone = FLAG_TONES[row.flag] || FLAG_TONES.idle
  return (
    <div className={`border-b border-slate-100 last:border-b-0 ${open ? "bg-slate-50/60" : ""}`}>
      <div
        role      = "button"
        tabIndex  = {0}
        onClick   = {onToggle}
        onKeyDown = {event => (event.key === "Enter" || event.key === " ") && (event.preventDefault(), onToggle())}
        className = {`${GRID} px-5 py-3 text-[12.5px] cursor-pointer hover:bg-slate-50`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <FontAwesomeIcon icon="chevron-right" className={`w-2.5 h-2.5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${tone.dot}`} title={t(`flags.${row.flag}`)} />
          <div className="min-w-0">
            <div className="line-clamp-2 font-semibold leading-snug text-slate-900" title={row.name}>{row.name}</div>
            <div className="truncate text-[11.5px] text-slate-500">
              {[row.client, row.manager].filter(Boolean).join(" · ")}
              {row.status !== "active" && <span className="ml-1.5 text-slate-400">· {row.status_label}</span>}
            </div>
          </div>
        </div>
        <Figures row={row} currency={currency} />
        <Sparkline values={row.trend} className="justify-end" />
      </div>
      {open && <ProjectDetail project={row} currency={currency} />}
    </div>
  )
}

const PortfolioTable = ({ projects = [], totals = {}, currency }) => {
  const [sort, setSort] = useState({ key: "drift", desc: true })
  const [openId, setOpenId] = useState(null)

  const rows = useMemo(() => {
    const value = SORTS[sort.key] || SORTS.drift
    return [...projects].sort((a, b) => {
      const left = value(a), right = value(b)
      const order = left < right ? -1 : left > right ? 1 : 0
      return sort.desc ? -order : order
    })
  }, [projects, sort])

  const onSort = key => setSort(current => ({ key, desc: current.key === key ? !current.desc : key !== "name" }))

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <div className="min-w-[1180px]">
        <div className={`${GRID} px-5 py-2.5 bg-slate-50 border-b border-slate-200`}>
          <div className="pl-[34px]"><HeaderCell sortKey="name" sort={sort} onSort={onSort} align="left">{t("columns.project")}</HeaderCell></div>
          <HeaderCell sortKey="sold"     sort={sort} onSort={onSort}>{t("columns.sold")}</HeaderCell>
          <HeaderCell sortKey="planned"  sort={sort} onSort={onSort}>{t("columns.planned")}</HeaderCell>
          <HeaderCell sortKey="done"     sort={sort} onSort={onSort}>{t("columns.done")}</HeaderCell>
          <HeaderCell sortKey="consumed" sort={sort} onSort={onSort} align="left">{t("columns.consumed_vs_progress")}</HeaderCell>
          <HeaderCell sortKey="performance" sort={sort} onSort={onSort}>{t("columns.performance")}</HeaderCell>
          <HeaderCell sortKey="projected" sort={sort} onSort={onSort}>{t("columns.projected")}</HeaderCell>
          <HeaderCell sortKey="drift"    sort={sort} onSort={onSort}>{t("columns.projected_variance")}</HeaderCell>
          <div className="text-right uppercase tracking-wider text-[10.5px] font-medium text-slate-500">{t("columns.labor_cost")}</div>
          <div className="text-right uppercase tracking-wider text-[10.5px] font-medium text-slate-500">{t("columns.trend")}</div>
        </div>

        {rows.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <FontAwesomeIcon icon="helmet-safety" className="text-slate-300 text-2xl mb-3" />
            <div className="text-[13px] text-slate-600 font-medium">{t("empty.title")}</div>
            <div className="text-[12.5px] text-slate-400 mt-1">{t("empty.hint")}</div>
          </div>
        ) : (
          rows.map(row => (
            <ProjectRow key={row.id} row={row} currency={currency} open={openId === row.id}
                        onToggle={() => setOpenId(current => (current === row.id ? null : row.id))} />
          ))
        )}

        {rows.length > 1 && (
          <div className={`${GRID} px-5 py-3 text-[12.5px] bg-slate-50 border-t border-slate-200 font-medium`}>
            <div className="pl-[34px] uppercase tracking-wider text-[10.5px] text-slate-500">{t("table.total", { count: rows.length })}</div>
            <Figures row={{ ...totals, progress: totals.sold ? (totals.earned / totals.sold) * 100 : 0, progress_known: !!totals.performance }} currency={currency} />
            <Sparkline values={totals.trend || []} className="justify-end" />
          </div>
        )}
      </div>
    </div>
  )
}

export default PortfolioTable
